import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Plus, RefreshCw, Search, Zap } from 'lucide-react';
import { Pen, Trash, Whatsapp } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { Button, Card, Container, Form, Pagination } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ADMIN_ROLE, ENDPOINTS, PROCESSOR_ROLE } from '../../helpers/constants';
import { isAdmin } from '../../helpers/global';
import AlertModal from '../AlertModal';
import DataTable from '../common/data-table/DataTable';
import TurnaroundTimeFormModal from './TurnaroundTimeFormModal';

const WHATSAPP_CONTACT_URL = 'https://wa.me/+13054033405';

const AVAILABLE_ON_CLOSERS_CHOICE_LOGO = 'https://accurateliensearch.net/assets/Closers-Choice-Logo-BFummXav.png';
const AVAILABLE_ON_QUALIA_LOGO = 'https://accurateliensearch.net/assets/AvailableOnQualia-light-1-IyA7bzNL.png';

const TURNAROUND_COLOR = {
  GREEN: '#16a34a',
  YELLOW: '#ca8a04',
  RED: '#dc2626'
};

const getTurnaroundText = (min, max, embeddedInBackOffice = false) => {
  if (min == null || max == null) return '—';
  const text = min === max ? `${min} days` : `${min}-${max} days`;
  const styleBase = embeddedInBackOffice ? {} : { fontWeight: 600 };
  if (max <= 2) {
    return <span style={{ color: TURNAROUND_COLOR.GREEN, ...styleBase }}>{text}</span>;
  }
  if (max <= 7) {
    return <span style={{ color: TURNAROUND_COLOR.YELLOW, ...styleBase }}>{text}</span>;
  }
  return <span style={{ color: TURNAROUND_COLOR.RED, ...styleBase }}>{text}</span>;
};

const getTurnaroundColumns = ({ canEdit, canDelete, rows, setFormModal, setDeleteId, embeddedInBackOffice }) => {
  const base = [
    {
      key: 'municipality',
      label: 'Municipality',
      sortable: true,
      align: 'left',
      width: '200px',
      render: (value, row) => (
        <span className="d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
          {value ?? '—'}
        </span>
      )
    },
    {
      key: 'county',
      label: 'County',
      sortable: true,
      align: 'left',
      width: '140px',
      render: (value, row) => (
        <span className="d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
          {value ?? '—'}
        </span>
      )
    },
    {
      key: 'standardMinDays',
      label: embeddedInBackOffice ? 'Standard' : 'Standard Turnaround',
      sortable: false,
      align: 'left',
      width: '130px',
      render: (value, row) => getTurnaroundText(row.standardMinDays, row.standardMaxDays, embeddedInBackOffice)
    },
    {
      key: 'rush',
      label: embeddedInBackOffice ? 'Rush' : 'Rush Available',
      sortable: false,
      align: 'left',
      width: '150px',
      render: (value, row) => {
        const hasRush = row.rush && row.rushMinDays != null && row.rushMaxDays != null;
        if (!hasRush) {
          return <span className="text-muted">—</span>;
        }
        return (
          <span className="d-inline-flex align-items-center">
            <Zap size={14} className="mr-1 flex-shrink-0 text-warning" aria-hidden />
            {getTurnaroundText(row.rushMinDays, row.rushMaxDays, embeddedInBackOffice)}
          </span>
        );
      }
    }
  ];

  const notesColumn = {
    key: 'notes',
    label: 'Notes',
    sortable: false,
    align: 'left',
    width: '260px',
    style: () => ({ whiteSpace: 'normal' }),
    render: (value, row) => {
      const text = (value ?? row.notes ?? '').toString().trim();
      if (!text) return <span className="text-muted">—</span>;
      return (
        <span className="small text-dark" style={{ wordBreak: 'break-word' }}>
          {text}
        </span>
      );
    }
  };

  if (!embeddedInBackOffice) {
    return [...base, notesColumn];
  }

  return [
    ...base,
    {
      key: 'updatedAt',
      label: 'Last Updated',
      sortable: false,
      align: 'left',
      width: '160px',
      render: (value, row) =>
        row.updatedAt ? (
          <span className="text-muted">{moment(row.updatedAt).fromNow()}</span>
        ) : (
          <span className="text-muted">—</span>
        )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      width: '100px',
      cellRenderer: row => (
        <div>
          <Button
            variant="outline-dark"
            className="px-1 py-0 mr-1"
            disabled={!canEdit}
            title={canEdit ? 'Edit' : 'View only'}
            onClick={e => {
              e.stopPropagation();
              if (!canEdit) return;
              const full = rows.find(r => r._id === row._id);
              if (full) setFormModal({ mode: 'edit', record: full });
            }}
          >
            <Pen size={10} />
          </Button>
          <Button
            variant="outline-danger"
            className="px-1 py-0 mr-1"
            disabled={!canDelete}
            title={canDelete ? 'Delete' : 'View only'}
            onClick={e => {
              e.stopPropagation();
              if (canDelete) setDeleteId(row._id);
            }}
          >
            <Trash size={10} />
          </Button>
        </div>
      )
    }
  ];
};

export const TurnaroundTimesContent = ({ embeddedInBackOffice = false, setActiveTabItemsMetadata, county = '' }) => {
  const [rows, setRows] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'municipality', direction: 'asc' });
  const debounceTimeoutRef = useRef(null);
  const [toLoadPageInfo, setToLoadPageInfo] = useState({ pageNumber: 1 });
  const [loadedPageInfo, setLoadedPageInfo] = useState({
    totalPages: 1,
    pageNumber: 1,
    resultCount: 0
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [formModal, setFormModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const role = typeof window !== 'undefined' ? localStorage.getItem('user-role') : '';
  const canEdit = isAdmin(role) || role === PROCESSOR_ROLE;
  const canDelete = role === ADMIN_ROLE;

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchInputChange = useCallback(e => {
    const v = e.target.value;
    setSearchInput(v);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchQuery(v.trim());
    }, 500);
  }, []);

  const columns = useMemo(
    () =>
      getTurnaroundColumns({
        canEdit,
        canDelete,
        rows,
        setFormModal,
        setDeleteId,
        embeddedInBackOffice
      }),
    [canEdit, canDelete, rows, embeddedInBackOffice]
  );

  const countyFilterValue = useMemo(() => {
    if (county == null || county === '') return '';
    const v = String(county).trim();
    return v;
  }, [county]);

  const turnaroundFilters = useMemo(() => {
    const filters = [];

    if (countyFilterValue) {
      filters.push({ key: 'county', value: countyFilterValue, type: 'string' });
    }
    return filters;
  }, [countyFilterValue]);

  useEffect(() => {
    setToLoadPageInfo(prev => ({ ...prev, pageNumber: 1 }));
  }, [searchQuery, sortConfig.key, sortConfig.direction, countyFilterValue]);

  const handleSortChange = useCallback(newSort => {
    setSortConfig(newSort);
  }, []);

  const fetchTurnaroundData = useCallback(
    async (pageNumber, append) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setRows([]);
        setIsFetchingData(true);
        if (setActiveTabItemsMetadata) {
          setActiveTabItemsMetadata({ count: 0, loading: true });
        }
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.TURNAROUND_TIMES_SEARCH,
        method: 'POST',
        requestBody: {
          query: searchQuery?.trim() || '',
          pageNumber,
          pageSize: 15,
          filters: turnaroundFilters,
          sortBy: sortConfig.key === 'county' ? 'county' : 'municipality',
          sortDir: sortConfig.direction === 'desc' ? 'desc' : 'asc'
        }
      });

      if (error) {
        toast.error(error);
        if (append) {
          setLoadingMore(false);
        } else {
          setIsFetchingData(false);
          if (setActiveTabItemsMetadata) {
            setActiveTabItemsMetadata({ count: 0, loading: false });
          }
        }
        return;
      }

      if (response) {
        const { pageNumber: responsePage, totalPages, results = [], resultCount, metadata } = response;
        setLoadedPageInfo({ pageNumber: responsePage, totalPages, resultCount });
        if (append) {
          setRows(prev => [...prev, ...results]);
        } else {
          setRows(results);
        }
        setLastUpdated(metadata?.lastUpdated);
        if (setActiveTabItemsMetadata) {
          setActiveTabItemsMetadata({ count: resultCount, loading: false });
        }
      }

      if (append) {
        setLoadingMore(false);
      } else {
        setIsFetchingData(false);
      }
    },
    [searchQuery, sortConfig, turnaroundFilters, setActiveTabItemsMetadata]
  );

  useEffect(() => {
    fetchTurnaroundData(toLoadPageInfo.pageNumber, false);
  }, [toLoadPageInfo.pageNumber, refreshKey, fetchTurnaroundData]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || isFetchingData) return;
    if (loadedPageInfo.pageNumber >= loadedPageInfo.totalPages) return;
    fetchTurnaroundData(loadedPageInfo.pageNumber + 1, true);
  }, [loadingMore, isFetchingData, loadedPageInfo, fetchTurnaroundData]);

  const bumpList = () => setRefreshKey(k => k + 1);

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteSubmitting(true);
    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.TURNAROUND_TIMES_WITH_ID(deleteId),
      method: 'DELETE'
    });
    setDeleteSubmitting(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success('Turnaround time deleted');
    setDeleteId(null);
    bumpList();
  };

  const handlePageChange = pageNumber => {
    setToLoadPageInfo({ ...toLoadPageInfo, pageNumber });
  };

  const tableData = useMemo(
    () =>
      rows.map(r => ({
        _id: r._id,
        municipality: r.municipality,
        county: r.county,
        standardMinDays: r.standardMinDays,
        standardMaxDays: r.standardMaxDays,
        rush: r.rush,
        rushMinDays: r.rushMinDays,
        rushMaxDays: r.rushMaxDays,
        notes: r.notes,
        updatedAt: r.updatedAt
      })),
    [rows]
  );

  const showPublicClient = !embeddedInBackOffice;

  const tableSection = (
    <div className="pb-4">
      {showPublicClient && (
        <div className="mb-4">
          <div className="d-flex flex-row justify-content-between align-items-start gap-4 my-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <img
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="Accurate"
                  width={140}
                  height={38}
                  className="d-block"
                />
              </div>
              <div className="d-flex align-items-center text-muted small">
                <RefreshCw size={14} className="mr-2 flex-shrink-0" aria-hidden />
                <span>Updated {lastUpdated ? moment(lastUpdated).format('MMMM D, YYYY') : '—'}</span>
              </div>
            </div>

            <div className="text-end" style={{ minWidth: 0, maxWidth: '100%' }}>
              <h1 className="h3 fw-bold text-dark mb-3">Live Municipality Turnaround Times</h1>
              <div className="d-flex flex-column align-items-end" style={{ gap: '0.35rem' }}>
                <div className="d-flex align-items-center gap-2 flex-shrink-0" style={{ color: '#4a5568' }}>
                  <Zap size={18} className="flex-shrink-0" fill="#FACC15" color="#FACC15" aria-hidden />
                  <span className="fw-medium" style={{ fontSize: '0.95rem', lineHeight: 1.35 }}>
                    Same-day processing for orders received before 5 PM EST
                  </span>
                </div>
                <p className="mb-0 text-end" style={{ fontSize: '0.75rem', lineHeight: 1.45, color: '#a0aec0' }}>
                  Listed turnaround times are based on municipality timelines.
                </p>
                <p className="mb-0 text-end" style={{ fontSize: '0.75rem', lineHeight: 1.45, color: '#a0aec0' }}>
                  Results are often delivered ahead of schedule.
                </p>
              </div>
            </div>
          </div>

          <div
            className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center w-100"
            style={{ gap: '10px' }}
          >
            <div className="position-relative flex-grow-1" style={{ minWidth: 0 }}>
              <Search
                className="icon-md position-absolute"
                style={{
                  left: 9,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6c757d'
                }}
              />
              <Form.Control
                type="text"
                size="lg"
                placeholder="Search by municipality or county..."
                className="bg-white search-input text-dark xsmallFont"
                value={searchInput}
                onChange={handleSearchInputChange}
                style={{ paddingLeft: 34, border: '1px solid #e5e7ea', fontWeight: 'normal', borderRadius: '8px' }}
              />
            </div>
            <div className="d-flex align-items-center flex-shrink-0" style={{ gap: 8, alignSelf: 'stretch' }}>
              <Link
                to="/new-order"
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center justify-content-center px-2 px-md-3 py-2 text-dark fw-semibold border-0 text-decoration-none"
                style={{
                  backgroundColor: '#F8CA40',
                  borderRadius: '8px',
                  gap: '6px'
                }}
              >
                New Order
                <ExternalLink size={16} aria-hidden />
              </Link>
              <Button
                variant="outline-success"
                size="md"
                className="d-flex align-items-center justify-content-center px-2 px-md-3 py-2 bg-white"
                style={{
                  borderRadius: '8px',
                  borderColor: '#4F8E5C',
                  color: '#4F8E5C',
                  gap: '6px'
                }}
                href={WHATSAPP_CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                as="a"
              >
                <Whatsapp size={16} aria-hidden />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      )}

      {!showPublicClient && (
        <div className="d-flex align-items-center mb-4 w-100" style={{ flexShrink: 0, gap: '8px' }}>
          <div className="position-relative me-2" style={{ flex: 1, marginRight: 4, minWidth: 0 }}>
            <Search
              className="icon-md position-absolute"
              style={{
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6c757d'
              }}
            />
            <Form.Control
              type="text"
              size="lg"
              placeholder="Search by municipality or county..."
              className="bg-white search-input text-dark xsmallFont"
              value={searchInput}
              onChange={handleSearchInputChange}
              style={{ paddingLeft: 40, border: '1px solid #e5e7ea', fontWeight: 'normal' }}
            />
          </div>
          {canEdit && embeddedInBackOffice && (
            <Button
              variant="success"
              size="sm"
              className="d-flex align-items-center"
              style={{
                minHeight: '40px'
              }}
              onClick={() => setFormModal({ mode: 'add' })}
            >
              <Plus size={14} className="mr-1" />
              Add New
            </Button>
          )}
        </div>
      )}

      <DataTable
        key={`${sortConfig.key}-${sortConfig.direction}`}
        data={tableData}
        columns={columns}
        rowKey="_id"
        pagination={false}
        sortable={true}
        defaultSort={sortConfig}
        onSortChange={handleSortChange}
        striped={false}
        hover={true}
        bordered={false}
        responsive={false}
        loading={isFetchingData}
        emptyMessage="Nothing to show"
        maxHeight="inherit"
        {...(!embeddedInBackOffice
          ? {
              maxHeight: '55vh',
              onBottomReached: handleLoadMore,
              loadingMoreData: loadingMore,
              bottomOffset: 100
            }
          : {})}
      />

      {embeddedInBackOffice && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={loadedPageInfo.pageNumber <= 1}>
              <span className="text-dark">&lt;&lt;</span>
            </Pagination.First>
            <Pagination.Prev
              linkClassName="text-dark"
              onClick={() => handlePageChange(loadedPageInfo.pageNumber - 1)}
              disabled={loadedPageInfo.pageNumber <= 1}
            >
              <span className="text-dark">&lt;</span>
            </Pagination.Prev>

            {Array.from({ length: Math.min(5, loadedPageInfo.totalPages) }, (_, i) => {
              let pageNum;
              if (loadedPageInfo.totalPages <= 5) {
                pageNum = i + 1;
              } else if (loadedPageInfo.pageNumber <= 3) {
                pageNum = i + 1;
              } else if (loadedPageInfo.pageNumber >= loadedPageInfo.totalPages - 2) {
                pageNum = loadedPageInfo.totalPages - 4 + i;
              } else {
                pageNum = loadedPageInfo.pageNumber - 2 + i;
              }

              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === loadedPageInfo.pageNumber}
                  onClick={() => handlePageChange(pageNum)}
                >
                  <span className="text-dark">{pageNum}</span>
                </Pagination.Item>
              );
            })}

            <Pagination.Next
              onClick={() => handlePageChange(loadedPageInfo.pageNumber + 1)}
              disabled={loadedPageInfo.pageNumber >= loadedPageInfo.totalPages}
            >
              <span className="text-dark">&gt;</span>
            </Pagination.Next>
            <Pagination.Last
              onClick={() => handlePageChange(loadedPageInfo.totalPages)}
              disabled={loadedPageInfo.pageNumber >= loadedPageInfo.totalPages}
            >
              <span className="text-dark">&gt;&gt;</span>
            </Pagination.Last>
          </Pagination>
        </div>
      )}

      {showPublicClient && (
        <Card
          className="mt-4 border-0"
          style={{
            borderRadius: '12px',
            backgroundColor: '#fff',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <Card.Body className="py-4 px-4">
            <div className="d-flex flex-column align-items-center justify-content-center text-center">
              <p
                className="text-muted mb-3 mb-md-4"
                style={{
                  fontSize: '0.7rem',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase'
                }}
              >
                Available on
              </p>
              <div
                className="d-flex flex-row flex-wrap align-items-center justify-content-center"
                style={{ gap: 'clamp(1.5rem, 4vw, 2.5rem)' }}
              >
                <img
                  src={AVAILABLE_ON_CLOSERS_CHOICE_LOGO}
                  alt="Closers' Choice - Closer Friendly Software Logo"
                  height={44}
                  style={{ maxWidth: 'min(100%, 240px)', width: 'auto', objectFit: 'contain' }}
                />
                <img
                  src={AVAILABLE_ON_QUALIA_LOGO}
                  alt="Available on Qualia"
                  height={44}
                  style={{ maxWidth: 'min(100%, 240px)', width: 'auto', objectFit: 'contain' }}
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  const modals = (
    <>
      <TurnaroundTimeFormModal
        show={formModal != null}
        mode={formModal?.mode ?? 'add'}
        initialRecord={formModal?.mode === 'edit' ? formModal.record : { county }}
        onHide={() => setFormModal(null)}
        onSaved={bumpList}
      />
      <AlertModal
        show={deleteId != null}
        onHide={() => !deleteSubmitting && setDeleteId(null)}
        onContinueClick={handleDeleteConfirm}
        onDismissClick={() => !deleteSubmitting && setDeleteId(null)}
        alertText="Are you sure you want to delete this turnaround time?"
        progressText="Deleting…"
        showProgress={deleteSubmitting}
        continueButtonText="Delete"
        continueButtonVariant="danger"
        dismissButtonText="Cancel"
      />
    </>
  );

  if (embeddedInBackOffice) {
    return (
      <>
        <Container fluid className="h-100 py-3 px-md-3">
          <div
            className="pt-3 pb-3"
            style={{
              paddingLeft: 24,
              paddingRight: 24
            }}
          >
            <div className="bg-white">{tableSection}</div>
          </div>
        </Container>
        {modals}
      </>
    );
  }

  return (
    <>
      <div className="bg-white">{tableSection}</div>
      {modals}
    </>
  );
};

const TurnaroundTimes = () => {
  return (
    <div className="mx-auto" style={{ minHeight: 'calc(100vh - 64px)', width: '65%' }}>
      <TurnaroundTimesContent />
    </div>
  );
};

export default TurnaroundTimes;
